import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, inject, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { IconComponent } from '@ui';
import { catchError, map, of, startWith } from 'rxjs';
import { LanguagesService } from '../../services/languages/languages.service';

@Component({
  selector: 'core-language-dropdown',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatSelectModule, MatFormFieldModule, MatOptionModule, IconComponent],
  templateUrl: './language-dropdown.component.html',
  styleUrl: './language-dropdown.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LanguageDropdownComponent),
      multi: true,
    },
  ],
})
export class LanguageDropdownComponent implements ControlValueAccessor {
  // Dependencies
  private readonly languagesService = inject(LanguagesService);

  // Inputs / Outputs
  @Input() languagesToFilterOut: string[] = [];
  @Input() placeholder = 'Select a language';
  @Output() languageSelected = new EventEmitter<string>();

  // State
  languages$ = this.languagesService.getLanguages().pipe(
    map((languages) => languages || []),
    catchError(() => of([])),
    startWith([])
  );

  selectedLanguage: string | null = null;
  disabled = false;
  touched = false;

  // ControlValueAccessor methods
  onChange = (value: string | null) => {};
  onTouched = () => {};

  onLanguageChange(languageCode: string): void {
    this.selectedLanguage = languageCode;
    this.onChange(languageCode);
    this.languageSelected.emit(languageCode);
    this.markAsTouched();
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string | null): void {
    this.selectedLanguage = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
